import React, { forwardRef, memo, useCallback } from 'react';
import { useAppDispatch, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import MultiProductBookingModal, {
  MultiProductBookingModalRef,
} from '@src/domains/products/features/multiProductBooking/MultiProductBookingModal';
import { abandonUpsellBooking, addUpsellItemsToCart } from '@src/stores/productUpsell';
import { selectProductForBooking } from '@src/stores/multiProductBooking';
import { useUpsellBooking } from './useUpsellBooking';

const UpsellBookingModal = forwardRef<MultiProductBookingModalRef>((_, ref) => {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const { products, totalCents } = useUpsellBooking();

  const handleSelect = useCallback(
    (productId: string) => {
      dispatch(selectProductForBooking(widgetId, productId));
    },
    [dispatch, widgetId],
  );

  const handleAddToCart = useCallback(() => {
    dispatch(addUpsellItemsToCart(widgetId));
  }, [dispatch, widgetId]);

  const handleConfirmAbandon = useCallback(() => {
    dispatch(abandonUpsellBooking());
  }, [dispatch]);

  return (
    <MultiProductBookingModal
      ref={ref}
      dataCy='upsell-booking-modal'
      title={i18n('upsell_booking_product_list_title')}
      products={products}
      onSelect={handleSelect}
      completedDescription={i18n('upsell_booking_completed_description')}
      totalCents={totalCents}
      onAddToCart={handleAddToCart}
      onConfirmAbandon={handleConfirmAbandon}
      cancelTitle={i18n('upsell_booking_cancel_title')}
      cancelDescription={i18n('upsell_booking_cancel_description')}
      cancelConfirmLabel={i18n('upsell_booking_cancel_action_yes')}
      cancelCancelLabel={i18n('upsell_booking_cancel_action_no')}
      alwaysConfirmClose
    />
  );
});

UpsellBookingModal.displayName = 'UpsellBookingModal';

export default memo(UpsellBookingModal);
